import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * ProtectedRoute component that handles route protection based on authentication and permissions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {Array<string>} props.requiredRoles - Array of roles that can access this route
 * @param {string} props.requiredPermissionScope - Required permission scope ('organization' or 'network_section')
 * @param {string} props.redirectTo - Path to redirect to if not authorized (default: '/login')
 */
const ProtectedRoute = ({ 
  children, 
  requiredRoles = [], 
  requiredPermissionScope = null,
  redirectTo = '/login'
}) => {
  const { isAuthenticated, canAccess, user } = useAuth();
  const location = useLocation();

  // If not authenticated, redirect to login with return URL
  if (!isAuthenticated) {
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // If authenticated but doesn't have required permissions, show access denied
  if (!canAccess(requiredRoles, requiredPermissionScope)) {
    return <AccessDenied user={user} requiredRoles={requiredRoles} />;
  }

  // User is authenticated and authorized, render the protected component
  return children;
};

/**
 * AccessDenied component to show when user doesn't have required permissions
 */
const AccessDenied = ({ user, requiredRoles }) => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
      <div className="mb-4">
        <svg
          className="mx-auto h-12 w-12 text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Access Denied
      </h2>
      <p className="text-gray-600 mb-4">
        You don't have permission to access this page.
      </p>
      <div className="text-sm text-gray-500 mb-4">
        <p>Your role: <span className="font-medium">{user?.role}</span></p>
        {requiredRoles.length > 0 && (
          <p>Required roles: <span className="font-medium">{requiredRoles.join(', ')}</span></p>
        )}
      </div>
      <button
        onClick={() => window.history.back()}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200"
      >
        Go Back
      </button>
    </div>
  </div>
);

export default ProtectedRoute; 